import React, { memo, useMemo, useCallback, useState } from 'react'

import { Icon, MoreChorizontal, RotatingSpinner, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, shape, arrayOf, oneOfType, number, func, bool } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import useDeleteModelSession from 'Pods/ThreeDStudio/Hooks/useDeleteModelSession'
import usePrivateModeAccess from 'Pods/ThreeDStudio/Hooks/usePrivateModeAccess'
import useUpdateRunPrivacy from 'Pods/ThreeDStudio/Hooks/useUpdateRunPrivacy'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import Dropdown from 'Pods/ThreeDStudio/Shared/Dropdown/Dropdown'

import ModelCardContent from './Components/ModelCardContent/ModelCardContent'

const size = { height: '14px', width: '14px' }
const spinnerSize = { height: '24px', width: '24px' }
const icon = <Icon notSpaced svgComponent={MoreChorizontal} color="dark" size={size} />

// the menu trigger wrapper (.cgt-dropdown-wrapper--with-backdrop) sits at z-index 452;
// keep the portal'd menu above it so tooltips overflowing the menu are not covered
const PORTAL_Z_INDEX = 453

function ModelCard({ sessionId, attributes, isProcessing = false, onNavigate }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [shouldCloseDropdown, setShouldCloseDropdown] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const { mainRun } = attributes || {}
  const { outputAssets, prompt, createdAt, id: mainRunId, title } = mainRun || {}
  const firstAsset = outputAssets?.[0]
  const { previewUrl } = firstAsset || {}

  const modelTitle = title ?? prompt

  // Active state and navigation share one target so the URL match mirrors exactly
  // where the card navigates. buildSharedRunPathname falls back to <title>-<id>
  // for legacy runs without a persisted slug, so this matches those too.
  const targetPathname = useMemo(() => (
    mainRun?.id != null
      ? buildSharedRunPathname({
        slug: mainRun.slug,
        id: mainRun.id,
        title: mainRun.title ?? mainRun.prompt,
      })
      : '/ai'
  ), [mainRun])
  const isActive = mainRun?.id != null && location?.pathname === targetPathname

  const isDraft = false

  const handleClick = useCallback(() => {
    if (location?.pathname !== targetPathname) {
      navigate(targetPathname)
    }
    onNavigate('model')
  }, [navigate, location, targetPathname, onNavigate])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }, [handleClick])

  const handleOpenDelete = useCallback(() => {
    setIsDeleteModalOpen(true)
    setShouldCloseDropdown(true)
  }, [])

  const handleCloseDelete = useCallback(() => {
    setIsDeleteModalOpen(false)
  }, [])

  const { deleteModel, isLoading: isDeleting } = useDeleteModelSession({ onSuccess: handleCloseDelete })

  const handleConfirmDelete = useCallback(() => {
    deleteModel(sessionId)
  }, [deleteModel, sessionId])

  const canTogglePrivacy = usePrivateModeAccess()
  const { updateRunPrivacy, isLoading: isPrivacyUpdating } = useUpdateRunPrivacy()
  const isPrivate = Boolean(mainRun?.private)

  const handleTogglePrivacy = useCallback(() => {
    updateRunPrivacy({ runId: mainRunId, sessionId, isPrivate: !isPrivate })
  }, [updateRunPrivacy, mainRunId, sessionId, isPrivate])

  const content = useMemo(() =>
    <ModelCardContent
      title={modelTitle}
      createdAt={createdAt}
      onDelete={handleOpenDelete}
      isDeleting={isDeleting}
      runId={mainRunId}
      isPrivate={isPrivate}
      canTogglePrivacy={canTogglePrivacy}
      onTogglePrivacy={handleTogglePrivacy}
      isPrivacyUpdating={isPrivacyUpdating}
    />,
    [
      modelTitle,
      createdAt,
      handleOpenDelete,
      isDeleting,
      mainRunId,
      isPrivate,
      canTogglePrivacy,
      handleTogglePrivacy,
      isPrivacyUpdating,
    ],
  )

  return (
    <>
      <div
        className={classnames(
          'model-card',
          isActive && 'model-card--active',
          isMenuOpen && 'model-card--menu-open',
          isProcessing && 'model-card--processing',
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {previewUrl && (
          <img
            className={classnames('model-card__image', isProcessing && 'model-card__image--processing')}
            src={previewUrl}
            alt={modelTitle}
          />
        )}
        {isProcessing && (
          <div className="model-card__processing">
            <Icon svgComponent={RotatingSpinner} size={spinnerSize} color="primary" notSpaced />
            <span className="model-card__processing-text">Processing...</span>
          </div>
        )}
        {isDraft && (
          <>
            <span className="model-card__status">Draft</span>
            <span className="model-card__title">{modelTitle}</span>
          </>
        )}
        {!isDraft && (
          <Dropdown
            icon={icon}
            content={content}
            placement="bottom-start"
            className="model-card__dropdown-trigger"
            contentClassName="model-card__dropdown-content"
            triggerCloseDropdown={shouldCloseDropdown}
            setTriggerCloseDropdown={setShouldCloseDropdown}
            onOpenChange={setIsMenuOpen}
            portalZIndex={PORTAL_Z_INDEX}
            usePortal
          />
        )}
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="model"
          itemName={modelTitle}
          isSubmitDisabled={isDeleting}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
    </>
  )
}

ModelCard.propTypes = {
  sessionId: oneOfType([string, number]).isRequired,
  attributes: shape({
    mainRun: shape({
      outputAssets: arrayOf(shape({
        previewUrl: string,
      })).isRequired,
      prompt: string,
      createdAt: string.isRequired,
      private: bool,
    }),
  }).isRequired,
  isProcessing: bool,
  onNavigate: func.isRequired,
}

export default memo(ModelCard)
